import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  FaCheck,
  FaEye,
  FaInbox,
  FaTimes,
} from "react-icons/fa";

import { Search, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import {
  fetchCandidateRequestData,
} from "../../services/user/api";

import ViewRequestDetails from "./ViewRequestDetails";
import ApproveRequestPage from "./ApproveRequestPage";
import DenyRequestPage from "./DenyRequestPage";

import "../../styles/user/requestManagement.css";

const RequestManagementPage = () => {
  const navigate = useNavigate();
  const getRequestId = (request) => {
  return (
    request?.requestId ??
    request?.redirectUrl ??
    null
  );
};
  const [requests, setRequests] = useState([]);
  const [search, setSearch] = useState("");
  const [entriesPerPage, setEntriesPerPage] =
    useState(10);
  const [currentPage, setCurrentPage] =
    useState(1);

  const [loading, setLoading] =
    useState(true);

  const [fetchError, setFetchError] =
    useState("");

  const [selectedRequest, setSelectedRequest] =
    useState(null);

  const [showViewModal, setShowViewModal] =
    useState(false);

  const [
    showApproveModal,
    setShowApproveModal,
  ] = useState(false);

  const [showDenyModal, setShowDenyModal] =
    useState(false);

  const [processing, setProcessing] =
    useState(false);

  const normalizeRequestType = (request) => {
    return String(
      request?.requestType ??
        request?.title ??
        request?.type ??
        "",
    )
      .trim()
      .toUpperCase()
      .replaceAll(" ", "_");
  };

  const normalizeStatus = (request) => {
    return String(
      request?.status ?? "PENDING",
    )
      .trim()
      .toUpperCase();
  };

  const isValidCandidateId = (value) => {
    if (!value) {
      return false;
    }

    return /^TRU\d+$/i.test(
      String(value).trim(),
    );
  };

const extractCandidateId = (request) => {
  const possibleCandidateIds = [
    request?.candidateId,
    request?.candidateid,
    request?.candidateID,
    request?.senderId,
    request?.sender_id,
  ];

  const directCandidateId =
    possibleCandidateIds.find((value) =>
      /^TRU\d+$/i.test(
        String(value ?? "").trim(),
      ),
    );

  if (directCandidateId) {
    return String(directCandidateId)
      .trim()
      .toUpperCase();
  }

  const message = String(
    request?.message ?? "",
  );

  const match =
    message.match(/\bTRU\d+\b/i);

  return match
    ? match[0].toUpperCase()
    : null;
};

  const getNotificationId = (request) => {
  return (
    request?.notificationId ??
    request?.id ??
    null
  );
};

  const getCandidateId = (request) => {
    return extractCandidateId(request) ?? "-";
  };

  const getRequestType = (request) => {
    return (
      request?.requestType ??
      request?.title ??
      request?.type ??
      "-"
    );
  };

  const formatRequestType = (
    requestType,
  ) => {
    if (!requestType) {
      return "-";
    }

    return String(requestType)
      .toLowerCase()
      .replaceAll("_", " ")
      .replace(/\b\w/g, (character) =>
        character.toUpperCase(),
      );
  };

  const formatDate = (dateValue) => {
    if (!dateValue) {
      return "-";
    }

    const date = new Date(dateValue);

    if (Number.isNaN(date.getTime())) {
      return String(dateValue);
    }

    return date.toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  };

  const extractResponseList = (response) => {
    const responseBody = response?.data;

    if (
      Array.isArray(
        responseBody?.data?.content,
      )
    ) {
      return responseBody.data.content;
    }

    if (Array.isArray(responseBody?.data)) {
      return responseBody.data;
    }

    if (
      Array.isArray(responseBody?.content)
    ) {
      return responseBody.content;
    }

    if (Array.isArray(responseBody)) {
      return responseBody;
    }

    return [];
  };

  const loadRequests = async () => {
    try {
      setLoading(true);
      setFetchError("");

      const response =
  await fetchCandidateRequestData();

      const requestList = extractResponseList(response);

      // console.log("Notification list:", requestList,);

      const normalizedRequests =
        requestList
          .map((request) => {
            const requestType =
              normalizeRequestType(request);

            const status = normalizeStatus(request);

            return {
  ...request,

  // client_notification_center primary key
  notificationId: request?.id ?? null,

  userId:
    request?.id ?? null,

  // candidate_requests primary key
  requestId:
    request?.redirectUrl ??
    request?.requestId ??
    null,

  candidateId:
    extractCandidateId(request),

  requestType,

  createdAt:
    request?.createdAt ??
    request?.requestDate ??
    request?.notificationTime ??
    null,

  status,
};
          });
          
      setRequests(normalizedRequests);
      setCurrentPage(1);
    } catch (error) {
      console.error(
        "Failed to fetch requests:",
        error,
      );

      console.error(
        "Backend response:",
        error?.response?.data,
      );

      const errorMessage =
        error?.response?.data?.message ??
        error?.response?.data?.error ??
        "Unable to fetch pending requests.";

      setFetchError(errorMessage);
      setRequests([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
  }, []);

  const filteredRequests = useMemo(() => {
    const searchValue = search
      .trim()
      .toLowerCase();

    if (!searchValue) {
      return requests;
    }

    return requests.filter((request) => {
      const requestId = String(
        getRequestId(request) ?? "",
      ).toLowerCase();

      const candidateId = String(
        getCandidateId(request) ?? "",
      ).toLowerCase();

      const requestType = String(
        getRequestType(request) ?? "",
      ).toLowerCase();

      const status = String(
        request?.status ?? "",
      ).toLowerCase();

      const message = String(
        request?.message ?? "",
      ).toLowerCase();

      return (
        requestId.includes(searchValue) ||
        candidateId.includes(searchValue) ||
        requestType.includes(searchValue) ||
        status.includes(searchValue) ||
        message.includes(searchValue)
      );
    });
  }, [requests, search]);

  const totalPages = Math.max(
    1,
    Math.ceil(
      filteredRequests.length /
        entriesPerPage,
    ),
  );

  const safeCurrentPage = Math.min(
    currentPage,
    totalPages,
  );

  const startIndex =
    (safeCurrentPage - 1) *
    entriesPerPage;

  const paginatedRequests =
    filteredRequests.slice(
      startIndex,
      startIndex + entriesPerPage,
    );

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setCurrentPage(1);
  };

  const handleEntriesChange = (event) => {
    setEntriesPerPage(
      Number(event.target.value),
    );

    setCurrentPage(1);
  };

  const openViewModal = (request) => {
    setSelectedRequest(request);
    setShowViewModal(true);
    setShowApproveModal(false);
    setShowDenyModal(false);
  };

  const openApproveModal = (request) => {
  const requestId = getRequestId(request);
  const notificationId =
    getNotificationId(request);

  if (
    notificationId === null ||
    notificationId === undefined
  ) {
    toast.error(
      "Notification ID is missing.",
    );
    return;
  }

  if (
    requestId === null ||
    requestId === undefined ||
    String(requestId).trim() === ""
  ) {
    toast.error("Request ID is missing.");
    return;
  }

  setSelectedRequest({
    ...request,

    // Required by existing ClientNotificationServiceImpl
    userId: Number(notificationId),
    requestId: Number(requestId),
  });

  setShowApproveModal(true);
  setShowViewModal(false);
  setShowDenyModal(false);
};

  const openDenyModal = (request) => {
  const requestId = getRequestId(request);
  const notificationId =
    getNotificationId(request);

  if (
    notificationId === null ||
    notificationId === undefined
  ) {
    toast.error(
      "Notification ID is missing.",
    );
    return;
  }

  if (
    requestId === null ||
    requestId === undefined ||
    String(requestId).trim() === ""
  ) {
    toast.error("Request ID is missing.");
    return;
  }

  setSelectedRequest({
    ...request,

    // Required by existing ClientNotificationServiceImpl
    userId: Number(notificationId),
    requestId: Number(requestId),
  });

  setShowDenyModal(true);
  setShowViewModal(false);
  setShowApproveModal(false);
};

  const closeAllModals = () => {
    if (processing) {
      return;
    }

    setShowViewModal(false);
    setShowApproveModal(false);
    setShowDenyModal(false);
    setSelectedRequest(null);
  };

  const removeRequestFromList = (
    completedRequest,
  ) => {
    const completedRequestId =
      getRequestId(completedRequest);

    setRequests((previousRequests) =>
      previousRequests.filter(
        (request) =>
          String(getRequestId(request)) !==
          String(completedRequestId),
      ),
    );

    setSelectedRequest(null);
  };

  const handleApproved = (approvedRequest) => {
  removeRequestFromList(approvedRequest);
  setShowApproveModal(false);
};

const handleDenied = (deniedRequest) => {
  removeRequestFromList(deniedRequest);
  setShowDenyModal(false);
};
  

 return (
  <div className="request-page">

    {/* Back Button */}
    <div className="request-back-wrapper">
      <button
        type="button"
        className="request-back-button"
        onClick={() => navigate(-1)}
      >
        <ArrowLeft size={18} />
        <span>Back</span>
      </button>
    </div>

    <div className="request-page-header">
      <h1>Approve/Deny Requests</h1>
    </div>

      <div className="request-page-content">
        <div className="request-toolbar">
          <div className="request-search-wrapper">
            <Search
              size={20}
              className="request-search-icon"
            />

            <input
              type="text"
              value={search}
              onChange={handleSearchChange}
              className="request-search-input"
              placeholder="Search requests..."
            />
          </div>

          <div className="request-entry-control">
            <span>Show</span>

            <select
              value={entriesPerPage}
              onChange={handleEntriesChange}
              className="request-entry-select"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>

            <span>entries</span>
          </div>
        </div>

        {loading && (
          <div className="request-page-message">
            Loading requests...
          </div>
        )}

        {!loading && fetchError && (
          <div className="request-page-message error">
            <p>{fetchError}</p>

            <button
              type="button"
              className="request-pagination-button"
              onClick={loadRequests}
            >
              Retry
            </button>
          </div>
        )}

        {!loading && !fetchError && (
          <div className="request-table-card">
            <div className="request-table-container">
              <table className="request-management-table">
                <thead>
                  <tr>
                    <th>Request ID</th>
                    <th>Candidate ID</th>
                    <th>Request Type</th>
                    <th>Request Date</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {paginatedRequests.length ===
                  0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="request-empty-cell"
                      >
                        <div className="request-empty-state">
                          <div className="request-empty-icon">
                            <FaInbox />
                          </div>

                          <p>
                            No pending requests
                            found.
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    paginatedRequests.map(
                      (request, index) => {
                        const requestId =
                          getRequestId(request);

                        const status =
                          normalizeStatus(
                            request,
                          );

                        const showActions =
                          request?.showActions !==
                            false &&
                          status === "PENDING";

                        return (
                          <tr
                            key={
                              requestId ??
                              `${getCandidateId(
                                request,
                              )}-${index}`
                            }
                          >
                            <td className="request-id-cell">
                              {requestId ?? "-"}
                            </td>

                            <td className="request-candidate-id">
                              {getCandidateId(
                                request,
                              )}
                            </td>

                            <td className="request-type-cell">
                              <span className="request-type-badge">
                                {formatRequestType(
                                  getRequestType(
                                    request,
                                  ),
                                )}
                              </span>
                            </td>

                            <td>
                              {formatDate(
                                request?.createdAt,
                              )}
                            </td>

                            <td>
                              <span className="request-status-badge">
                                {status}
                              </span>
                            </td>

                            <td>
                              <div className="request-table-actions">
                                <button
                                  type="button"
                                  className="request-icon-button view"
                                  title="View request"
                                  onClick={() =>
                                    openViewModal(
                                      request,
                                    )
                                  }
                                >
                                  <FaEye />
                                </button>

                                {showActions && (
                                  <>
                                    <button
                                      type="button"
                                      className="request-action-button approve"
                                      disabled={
                                        processing
                                      }
                                      onClick={() =>
                                        openApproveModal(
                                          request,
                                        )
                                      }
                                    >
                                      <FaCheck />
                                      Approve
                                    </button>

                                    <button
                                      type="button"
                                      className="request-action-button deny"
                                      disabled={
                                        processing
                                      }
                                      onClick={() =>
                                        openDenyModal(
                                          request,
                                        )
                                      }
                                    >
                                      <FaTimes />
                                      Deny
                                    </button>
                                  </>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      },
                    )
                  )}
                </tbody>
              </table>
            </div>

            <div className="request-pagination">
              <button
                type="button"
                className="request-pagination-button"
                disabled={
                  safeCurrentPage === 1
                }
                onClick={() =>
                  setCurrentPage(
                    (previousPage) =>
                      Math.max(
                        previousPage - 1,
                        1,
                      ),
                  )
                }
              >
                Previous
              </button>

              <span className="request-pagination-info">
                Page {safeCurrentPage} of{" "}
                {totalPages}
              </span>

              <button
                type="button"
                className="request-pagination-button"
                disabled={
                  safeCurrentPage ===
                  totalPages
                }
                onClick={() =>
                  setCurrentPage(
                    (previousPage) =>
                      Math.min(
                        previousPage + 1,
                        totalPages,
                      ),
                  )
                }
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {showViewModal &&
        selectedRequest && (
          <ViewRequestDetails
            request={selectedRequest}
            onClose={closeAllModals}
          />
        )}

      <ApproveRequestPage
        request={selectedRequest}
        isOpen={showApproveModal}
        onClose={closeAllModals}
        onApproved={handleApproved}
        processing={processing}
        setProcessing={setProcessing}
        formatRequestType={
          formatRequestType
        }
      />

      <DenyRequestPage
        request={selectedRequest}
        isOpen={showDenyModal}
        onClose={closeAllModals}
        onDenied={handleDenied}
        processing={processing}
        setProcessing={setProcessing}
        formatRequestType={
          formatRequestType
        }
      />
    </div>
  );
};

export default RequestManagementPage;